// src/lib/supabase/queries/stream-queries.ts
/**
 * .tuMetrics {"tu1": 0.88, "tu2": 0.89, "tu3": 7, "notes": "Stream message mappers + time-based pagination queries; TOAST-safe content mapping", "tuVersion": "2.5"}
 * .help "Single source of truth for stream message reads/writes: row mapping, latest-first pagination, and CRUD."
 */
// Purpose: Single source of truth for stream (comments) reads/writes.
// Design:
//  - Mapping helpers (DB <-> App types)
//  - Low-level query builders (DM vs non-DM)
//  - Stable pagination helpers (latest-first + time-based "load older")
//  - Legacy offset-based fetch kept for compatibility
//  - CRUD (insert/update/delete)
//
// ⚠️ Paging strategy (recommended for UI):
//  - Initial load: fetch **most recent N** (DESC), then reverse to display ASC
//  - Load more: fetch **older than earliest currently loaded** (created_at < earliest.createdAt), ASC, then prepend
//
// Rationale: Offset-from-0 on long conversations returns the oldest slice and hides recent messages,
// leading to “time travel” when navigating away/back. Time-based pagination avoids that.

import { supabase } from '../../supabase';
import { Comment } from '../../../types';
import type { Database } from '../../../types/supabase';

/* -------------------------------------------------------------------------------------------------
 * Types
 * ------------------------------------------------------------------------------------------------ */
type Json = string | number | boolean | null | { [key: string]: Json | undefined } | Json[];

type CommentRow = Database['public']['Tables']['comments']['Row'];
type CommentInsert = Database['public']['Tables']['comments']['Insert'];
type CommentUpdate = Database['public']['Tables']['comments']['Update'];

/* -------------------------------------------------------------------------------------------------
 * JSONB helpers
 * ------------------------------------------------------------------------------------------------ */
const safeParseJsonb = <T>(jsonbData: string | Json | null): T | undefined => {
  if (jsonbData === null || jsonbData === undefined) return undefined;
  try {
    return (typeof jsonbData === 'string' ? JSON.parse(jsonbData) : jsonbData) as T;
  } catch (e) {
    console.error('Error parsing JSONB data:', e, 'Data:', jsonbData);
    return undefined;
  }
};

/* -------------------------------------------------------------------------------------------------
 * Mappers (DB <-> App)
 * ------------------------------------------------------------------------------------------------ */
export const mapCommentFromDB = (row: CommentRow): Comment => ({
  id: row.id,
  userId: row.user_id,
  entityType: row.entity_type as Comment['entityType'],
  entityId: row.entity_id,
  // July 2026: never String(undefined) → the literal text "undefined".
  // Realtime UPDATE payloads omit unchanged TOASTed columns (content > ~2KB),
  // so row.content can legitimately be absent — map it to '' and let
  // mergeSafe keep the existing text.
  content: row.content == null ? '' : String(row.content),
  createdAt: row.created_at,
  updatedAt: row.updated_at,
  parentCommentId: row.parent_comment_id || undefined,
  mentions: row.mentions || [],
  reactions: safeParseJsonb<Record<string, string[]>>(row.reactions) || {},
  companyId: row.company_id || undefined,
  streamType: (row.stream_type as Comment['streamType']) || undefined,
  recipientStreamId: row.recipient_stream_id || undefined,
  sharedItemId: row.shared_item_id || undefined,
  sharedItemDetails: safeParseJsonb<Comment['sharedItemDetails']>(row.shared_item_details),
  tags: row.tags || [],
});

export const mapCommentToDB = (
  comment: Omit<Comment, 'id' | 'createdAt' | 'updatedAt'>
): CommentInsert => ({
  user_id: comment.userId,
  entity_type: comment.entityType,
  entity_id: comment.entityId,
  content: comment.content,
  parent_comment_id: comment.parentCommentId || null,
  mentions: comment.mentions,
  reactions: comment.reactions,
  company_id: comment.companyId || null,
  stream_type: comment.streamType || null,
  recipient_stream_id: comment.recipientStreamId || null,
  shared_item_id: comment.sharedItemId || null,
  shared_item_details: comment.sharedItemDetails
    ? JSON.stringify(comment.sharedItemDetails)
    : null,
  tags: comment.tags || [],
});

export const mapCommentUpdatesToDB = (updates: Partial<Comment>): CommentUpdate => {
  const dbUpdates: CommentUpdate = {};
  if (updates.content !== undefined) dbUpdates.content = updates.content;
  if (updates.reactions !== undefined) dbUpdates.reactions = updates.reactions;
  if (updates.tags !== undefined) dbUpdates.tags = updates.tags;
  if (updates.sharedItemDetails !== undefined) {
    dbUpdates.shared_item_details = updates.sharedItemDetails
      ? JSON.stringify(updates.sharedItemDetails)
      : null;
  }
  return dbUpdates;
};

/* -------------------------------------------------------------------------------------------------
 * Core query builder (DM vs non-DM)
 *  - Central place to keep parity between all fetchers
 * ------------------------------------------------------------------------------------------------ */
const baseCommentsSelect = () =>
  supabase.from('comments').select('*'); // caller must add filters

const applyCompanyFilter = (q: ReturnType<typeof baseCommentsSelect>, companyId: string) =>
  q.eq('company_id', companyId);

/**
 * Build a query for a given stream context.
 * - direct_message: symmetric (A→B or B→A), and stream_type=direct_message
 * - others: recipient_stream_id + explicit stream_type
 */
const buildStreamQuery = (
  companyId: string,
  streamType: Comment['streamType'],
  recipientStreamId: string,
  currentUserId: string
) => {
  let query = applyCompanyFilter(baseCommentsSelect(), companyId);

  if (streamType === 'direct_message') {
    query = query.or(
      `and(user_id.eq.${currentUserId},recipient_stream_id.eq.${recipientStreamId},stream_type.eq.direct_message),` +
        `and(user_id.eq.${recipientStreamId},recipient_stream_id.eq.${currentUserId},stream_type.eq.direct_message)`
    );
  } else {
    query = query
      .eq('recipient_stream_id', recipientStreamId)
      .eq('stream_type', streamType);
  }

  // December 11, 2025: Filter out system messages (context updates, etc)
  // These are for agent inbox manager triggers, not for user display
  query = query.not('tags', 'cs', '{system-notification}');
  query = query.not('tags', 'cs', '{context-update}');

  return query;
};

/* -------------------------------------------------------------------------------------------------
 * RECOMMENDED READ HELPERS (latest-first + time-based)
 * ------------------------------------------------------------------------------------------------ */

/**
 * Fetch the most recent N rows for a stream (newest first).
 * UI can reverse to display ascending.
 */
export const fetchLatestStreamMessages = async (
  companyId: string,
  streamType: Comment['streamType'],
  recipientStreamId: string,
  currentUserId: string,
  limit: number
): Promise<Comment[]> => {
  const { data, error } = await buildStreamQuery(
    companyId,
    streamType,
    recipientStreamId,
    currentUserId
  )
    .order('created_at', { ascending: false })
    .range(0, Math.max(0, limit - 1));

  if (error) throw error;
  return (data || []).map(mapCommentFromDB);
};

/**
 * Fetch rows older than a given timestamp (oldest→newest within the page),
 * suitable for "Load older" paging to prepend to the top of the timeline.
 */
export const fetchMessagesBefore = async (
  companyId: string,
  streamType: Comment['streamType'],
  recipientStreamId: string,
  currentUserId: string,
  beforeIso: string,
  limit: number
): Promise<Comment[]> => {
  const { data, error } = await buildStreamQuery(
    companyId,
    streamType,
    recipientStreamId,
    currentUserId
  )
    .lt('created_at', beforeIso)
    .order('created_at', { ascending: true })
    .range(0, Math.max(0, limit - 1));

  if (error) throw error;
  return (data || []).map(mapCommentFromDB);
};

/* -------------------------------------------------------------------------------------------------
 * LEGACY/OFFSET FETCH (kept for compatibility)
 *  - Offset-from-0 returns oldest slice; avoid for long histories.
 * ------------------------------------------------------------------------------------------------ */
export const fetchStreamMessages = async (
  companyId: string,
  streamType: Comment['streamType'],
  recipientStreamId: string,
  currentUserId: string,
  limit: number,
  offset: number
): Promise<Comment[]> => {
  const { data, error } = await buildStreamQuery(
    companyId,
    streamType,
    recipientStreamId,
    currentUserId
  )
    .order('created_at', { ascending: true })
    .range(offset, offset + limit - 1);

  if (error) throw error;
  return (data || []).map(mapCommentFromDB);
};

/* -------------------------------------------------------------------------------------------------
 * Writes (CRUD)
 * ------------------------------------------------------------------------------------------------ */

// Insert (optionally accept client-generated id)
export const insertStreamMessage = async (commentData: CommentInsert & { id?: string }) => {
  const { id, ...rest } = commentData;

  const { data, error } = await supabase
    .from('comments')
    .insert({
      ...(id && { id }),
      ...rest,
    })
    .select()
    .single();

  if (error) throw error;
  return mapCommentFromDB(data);
};

// Update by id (DB RLS controls permissions)
export const updateStreamMessageById = async (commentId: string, updates: CommentUpdate) => {
  const { data, error } = await supabase
    .from('comments')
    .update(updates)
    .eq('id', commentId)
    .select()
    .single();

  if (error) {
    console.error('updateStreamMessageById: Supabase error:', error);
    throw error;
  }
  return mapCommentFromDB(data);
};

// Delete by id
export const deleteStreamMessageById = async (commentId: string) => {
  const { error } = await supabase.from('comments').delete().eq('id', commentId);
  if (error) throw error;
  return true;
};